import { useCallback, useEffect, useState } from 'react';
import type { EmailTemplate } from '@epasu/core';
import { Pencil, Plus, X } from 'lucide-react';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { TemplateDialog } from '@/components/TemplateDialog';

interface Props {
  campaignId: string;
  onChanged: () => void;
}

interface SchrittZeile {
  delayDays: number;
  templateId: string | null;
}

/**
 * Der Schritte-Reiter: Erstansprache plus Nachfass-Stufen, je mit Vorlage.
 *
 * Schritt 1 ist immer die Erstansprache — seine Wartezeit steht nirgends
 * geschrieben (`naechsteWartezeit` in `engine/followups.ts` liest nur die
 * Wartezeit ab Schritt 2), deshalb zeigt seine Zeile keine Wartezeit und
 * lässt sich nicht entfernen.
 *
 * Die Vorlage lässt sich hier auch schreiben, nicht nur auswählen. Vorher
 * führte der einzige Weg über Einstellungen → Vorlagen, und wer eine Kampagne
 * anlegte, stand vor einem Auswahlfeld mit „— keine Vorlage —“ als einzigem
 * Eintrag, ohne einen Hinweis, wohin.
 */
export function CampaignStepsTab({ campaignId, onChanged }: Props) {
  const [schritte, setSchritte] = useState<SchrittZeile[] | null>(null);
  /** Der zuletzt geladene oder gespeicherte Stand — Grundlage für „ungespeichert“. */
  const [stand, setStand] = useState('');
  const [vorlagen, setVorlagen] = useState<EmailTemplate[]>([]);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);
  const [neuGestellt, setNeuGestellt] = useState<string | null>(null);
  /** Index der Zeile, deren Vorlage gerade verfasst oder bearbeitet wird. */
  const [verfassen, setVerfassen] = useState<number | null>(null);

  const laden = useCallback(() => {
    setFehler(null);
    setGespeichert(false);
    crm.campaigns.steps(campaignId).then(
      (rows) => {
        const zeilen =
          rows.length > 0
            ? rows.map((r) => ({ delayDays: r.delayDays, templateId: r.templateId }))
            : [{ delayDays: 0, templateId: null }];
        setSchritte(zeilen);
        setStand(JSON.stringify(zeilen));
      },
      (err: unknown) => setFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen'),
    );
  }, [campaignId]);
  useEffect(laden, [laden]);

  const ladeVorlagen = useCallback(() => {
    void crm.templates.list().then(setVorlagen);
  }, []);
  useEffect(ladeVorlagen, [ladeVorlagen]);

  if (!schritte) {
    return <p className="text-sm text-muted-foreground">Lade …</p>;
  }

  const ungespeichert =
    JSON.stringify(
      schritte.map((s, i) => ({ delayDays: i === 0 ? 0 : s.delayDays, templateId: s.templateId })),
    ) !== stand;

  const setZeile = (index: number, patch: Partial<SchrittZeile>) =>
    setSchritte((rows) => (rows ?? []).map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const nachfassenNeuStellen = async () => {
    setBusy(true);
    setFehler(null);
    setNeuGestellt(null);
    try {
      const r = await crm.campaigns.rearmFollowups(campaignId);
      setNeuGestellt(
        `${r.neuGestellt} Mail(s) neu eingeplant, ${r.beantwortet} wegen Antwort abgeschlossen.`,
      );
      onChanged();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Neu einplanen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const speichern = async () => {
    setBusy(true);
    setFehler(null);
    setGespeichert(false);
    try {
      const gesendet = schritte.map((s, i) => ({
        delayDays: i === 0 ? 0 : s.delayDays,
        templateId: s.templateId,
      }));
      await crm.campaigns.setSteps(campaignId, gesendet);
      setStand(JSON.stringify(gesendet));
      setGespeichert(true);
      onChanged();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="space-y-3 p-6">
      {schritte.map((s, i) => (
        <div key={i} className="flex items-center gap-3 rounded-md border p-3">
          <span className="w-32 shrink-0 text-sm font-medium">
            {/* Schritt 1 (i = 0) ist die Erstansprache; das i-te Nachfassen
                liegt bei Schrittnummer i + 1 — dieselbe Zaehlung wie
                `FollowupRow.nextStep` und `FollowupSendDueDialog`. */}
            {i === 0 ? '1. Erstansprache' : `${i}. Nachfassen`}
          </span>

          {i > 0 && (
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
              nach
              <Input
                className="h-9 w-16"
                inputMode="numeric"
                value={s.delayDays}
                onChange={(e) => setZeile(i, { delayDays: Number(e.target.value) || 0 })}
              />
              Tagen
            </label>
          )}

          <Select
            className="flex-1"
            value={s.templateId ?? ''}
            onChange={(e) => setZeile(i, { templateId: e.target.value || null })}
          >
            <option value="">— keine Vorlage —</option>
            {vorlagen.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </Select>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => setVerfassen(i)}
          >
            <Pencil className="mr-1.5 h-4 w-4" />
            {s.templateId ? 'Bearbeiten' : 'Verfassen'}
          </Button>

          {i > 0 && (
            <button
              type="button"
              aria-label="Schritt entfernen"
              className="shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => setSchritte((rows) => (rows ?? []).filter((_, idx) => idx !== i))}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() =>
          setSchritte((rows) => [...(rows ?? []), { delayDays: 3, templateId: null }])
        }
      >
        <Plus className="mr-1.5 h-4 w-4" />
        Nachfass-Stufe
      </Button>

      {/*
        Ohne Vorlage riegelt `welleSenden` ab (WaveBlocker 'keine_vorlage') —
        die Kampagne versendet dann gar nichts, und mit Zeitplan faellt das
        sonst tagelang niemandem auf.
      */}
      {schritte[0]?.templateId == null && (
        <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
          Der Erstansprache fehlt die Vorlage — solange sie fehlt, verschickt diese
          Kampagne nichts. „Verfassen“ legt sie an.
        </p>
      )}

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {gespeichert && !fehler && <p className="text-sm text-muted-foreground">Gespeichert.</p>}

      {/*
        Eine verfasste Vorlage haengt bis hierhin nur im Zustand dieser Seite.
        Wer den Dialog schliesst und weggeht, haette sie sonst geschrieben und
        doch nicht zugeordnet — ohne dass irgendwo stuende, dass etwas fehlt.
      */}
      <div className="flex items-center justify-end gap-3">
        {neuGestellt && <span className="text-sm text-muted-foreground">{neuGestellt}</span>}
        <Button
          type="button"
          variant="outline"
          onClick={() => void nachfassenNeuStellen()}
          disabled={busy || ungespeichert}
          title="Bereits versendete Mails dieser Kampagne bekommen die Fälligkeit der nächsten Stufe"
        >
          Nachfassen für versendete Mails neu einplanen
        </Button>
        {ungespeichert && (
          <span className="text-sm text-warning">Noch nicht gespeichert</span>
        )}
        <Button onClick={() => void speichern()} disabled={busy || !ungespeichert}>
          {busy ? 'Speichere …' : 'Schritte speichern'}
        </Button>
      </div>

      {verfassen !== null && (
        <TemplateDialog
          template={
            vorlagen.find((v) => v.id === schritte[verfassen]?.templateId) ?? null
          }
          onClose={() => setVerfassen(null)}
          onSaved={(vorlage) => {
            // Eine frisch verfasste Vorlage steht sofort in ihrer Zeile — sie
            // erst noch aus der Liste heraussuchen zu muessen, waere der halbe
            // Umweg, den dieser Knopf abschafft.
            setZeile(verfassen, { templateId: vorlage.id });
            setVerfassen(null);
            ladeVorlagen();
          }}
        />
      )}
    </Card>
  );
}
